/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import { View, Text, Icon, Pressable } from '@components/atoms'
import dayjs from 'dayjs'
import { IconButton, Tooltip, Chip } from '@mui/material'
import { Edit, Delete } from '@mui/icons-material'
import { blue, deepPurple, indigo, grey } from '@mui/material/colors'
import { ProductType } from '@schemas'

interface ProductListItemProps extends ProductType {
	onClick?: () => void
	onDelete?: () => void
	onEdit?: () => void
	isSelected?: boolean
}

export const ProductListItem = ({
	id,
	code,
	name: productName,
	usd: price,
	useYn: isActive,
	createdAt,
	onClick,
	onDelete,
	onEdit,
	isSelected = false,
}: ProductListItemProps) => {
	return (
		<View
			css={style}
			className={isSelected ? 'selected' : isActive ? 'completed' : 'pending'}
			onClick={onClick}
		>
			<Pressable
				className={
					'productItemButton ' +
					(isSelected ? 'selected' : isActive ? 'completed' : 'pending')
				}
				onClick={onClick}
			>
				{isSelected && (
					<View className="selectedIndicatorView">
						<Icon name="check-bold" className="selectedIndicatorIcon" />
					</View>
				)}
				<View className="buttonContainer">
					<Tooltip title="Edit">
						<IconButton
							size="small"
							onClick={e => {
								e.stopPropagation()
								onEdit?.()
							}}
						>
							<Edit fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Delete">
						<IconButton
							size="small"
							onClick={e => {
								e.stopPropagation()
								onDelete?.()
							}}
						>
							<Delete fontSize="small" />
						</IconButton>
					</Tooltip>
				</View>

				<View className="contentView">
					<View className="header">
						<Text className="title">{productName}</Text>
						{code !== '' && (
							<Text className="productCode" category="p1">{`(${code})`}</Text>
						)}
						<Chip
							label={isActive ? '사용중' : '미사용'}
							size="small"
							className={`status ${isActive ? 'active' : 'inactive'}`}
						/>
					</View>

					<View className="contentInfoView">
						<Text category="p2" hint="500" className="contentInfoLabel">
							제품명 :
						</Text>
						<Text category="s2" className="contentInfoValue">
							{productName}
						</Text>
					</View>
					<View className="contentInfoView">
						<Text category="p2" hint="500" className="contentInfoLabel">
							가격 :
						</Text>
						<Text category="s2" className="contentInfoValue">
							{price.toLocaleString('en-US', {
								style: 'currency',
								currency: 'USD',
							})}
						</Text>

						<Text category="p2" className="contentInfoValue">
							{`(${(price / 1.1).toLocaleString('en-US', {
								style: 'currency',
								currency: 'USD',
							})})`}
						</Text>
					</View>
					<View className="contentInfoView">
						<Text category="p2" hint="500" className="contentInfoLabel">
							제작일자 :
						</Text>
						<Text category="s2" className="contentInfoValue">
							{dayjs(createdAt).format('YYYY-MM-DD')}
						</Text>
					</View>
				</View>
			</Pressable>
		</View>
	)
}

const style = css({
	position: 'relative',
	display: 'flex',
	marginBottom: '0.75rem',

	'.productItemButton': {
		width: '100%',
		height: 'fit-content',
		padding: '0.75rem',
		borderRadius: '5px',
		boxShadow: '0px 2px 5px 0px rgb(0 0 0 / 25%)',
		borderLeft: '10px solid',
		transition: 'all 0.2s ease-in-out',
		background: 'white',

		'&:hover': {
			backgroundColor: grey[50],
			boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
		},

		'&.completed': {
			borderLeftColor: blue['A400'],
		},

		'&.pending': {
			borderLeftColor: grey[400],
		},

		'&.selected': {
			borderColor: deepPurple['A400'],
			borderLeftColor: deepPurple['A400'],
			background: '#eee7f6',

			'.contentView': {
				'.header': {
					'.title': {
						color: deepPurple['A400'],
					},

					'.status': {
						backgroundColor: `${deepPurple['A400']} !important`,
						color: 'white !important',
					},
				},
			},

			'.contentInfoView': {
				'.contentInfoLabel': {
					color: indigo[500],
				},

				'.contentInfoValue': {
					color: deepPurple['A400'],
				},
			},
		},

		'.selectedIndicatorView': {
			position: 'absolute',
			width: '1.3rem',
			height: '1.3rem',
			top: 0,
			bottom: 0,
			left: '-0.5rem',
			margin: 'auto',
			background: 'white',
			border: '1px solid',
			borderColor: deepPurple['A400'],
			borderRadius: '50%',

			'.selectedIndicatorIcon': {
				color: deepPurple['A400'],
				fontSize: '18px',
			},
		},

		'.buttonContainer': {
			position: 'absolute',
			top: '0.75rem',
			right: '0.75rem',
			display: 'flex',
			gap: '0.25rem',
		},
		'.contentView': {
			flex: 1,
			display: 'flex',
			flexDirection: 'column',

			'.header': {
				display: 'flex',
				gap: '0.5rem',
				alignItems: 'center',
				paddingRight: '4rem',
				height: '2.25rem',

				'.title': {
					fontSize: '1.25rem',
					fontWeight: 600,
					color: '#111827',
				},

				'.status': {
					fontSize: '0.75rem',
					fontWeight: 500,

					'&.active': {
						backgroundColor: blue[100],
						color: blue['A400'],
					},
					'&.inactive': {
						backgroundColor: grey[200],
						color: grey[700],
					},
				},
			},

			'.contentInfoView': {
				display: 'flex',
				alignItems: 'center',
				gap: '0.25rem',
				height: '1rem',

				'.contentInfoLabel': {
					margin: 0,
				},
				'.contentInfoValue': {
					margin: 0,
				},
			},
		},
	},
})
